/**
 * CetusEd Admin Console — Platform-Wide Announcements
 *
 * COMPLIANCE: Announcements are non-PII communication to districts.
 *   Maintenance announcements inform districts of scheduled downtime.
 *   Security announcements must be sent immediately for active incidents.
 */

import React, { useState, useCallback } from 'react';
import { useAnnouncements, useCreateAnnouncement } from '../../api/hooks';

export const Announcements: React.FC = () => {
  const { data: announcements, isLoading, error } = useAnnouncements();
  const createAnnouncement = useCreateAnnouncement();

  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState<'info' | 'warning' | 'maintenance'>('info');
  const [targetAudience, setTargetAudience] = useState<'all' | 'admins' | 'district'>('all');

  const handleCreate = useCallback(() => {
    if (!title.trim() || !body.trim()) return;
    // mutate() routes failures into createAnnouncement.isError, which is
    // rendered below — no swallowed rejection, no invisible failure.
    createAnnouncement.mutate(
      {
        title: title.trim(),
        body: body.trim(),
        severity,
        targetAudience,
      },
      {
        onSuccess: () => {
          setShowForm(false);
          setTitle('');
          setBody('');
        },
      },
    );
  }, [title, body, severity, targetAudience, createAnnouncement]);

  if (isLoading) return <div className="admin-loading">Loading announcements...</div>;

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>Announcements</h1>
        <button className="admin-btn admin-btn--primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : 'New Announcement'}
        </button>
      </div>

      {showForm && (
        <div className="admin-form announcement-form">
          <h2>Create Announcement</h2>
          <div className="form-field">
            <label htmlFor="ann-title">Title</label>
            <input id="ann-title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>
          <div className="form-field">
            <label htmlFor="ann-body">Body</label>
            <textarea id="ann-body" value={body} onChange={(e) => setBody(e.target.value)} required rows={4} />
          </div>
          <div className="form-field">
            <label htmlFor="ann-severity">Severity</label>
            <select id="ann-severity" value={severity} onChange={(e) => setSeverity(e.target.value as typeof severity)}>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="ann-audience">Target Audience</label>
            <select id="ann-audience" value={targetAudience} onChange={(e) => setTargetAudience(e.target.value as typeof targetAudience)}>
              <option value="all">All Users</option>
              <option value="admins">Admins Only</option>
              <option value="district">District Staff</option>
            </select>
          </div>
          {createAnnouncement.isError && (
            <div className="admin-error" role="alert">
              Failed to publish announcement. Please try again.
            </div>
          )}
          <div className="form-actions">
            <button className="admin-btn admin-btn--primary" onClick={handleCreate} disabled={createAnnouncement.isPending}>
              {createAnnouncement.isPending ? 'Creating...' : 'Publish'}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="admin-error" role="alert">
          Unable to load announcements.
        </div>
      )}

      {announcements && announcements.length === 0 && (
        <p className="empty-state">No announcements published.</p>
      )}

      {announcements && announcements.length > 0 && (
        <div className="announcement-list">
          {announcements.map((ann) => (
            <div key={ann.id} className={`announcement-card announcement-card--${ann.severity}`}>
              <div className="announcement-header">
                <span className={`severity-tag severity-tag--${ann.severity}`}>
                  {ann.severity.toUpperCase()}
                </span>
                {ann.published ? (
                  <span className="announcement-date">
                    Published {new Date(ann.publishedAt || ann.createdAt).toLocaleDateString()}
                  </span>
                ) : (
                  <span className="announcement-draft">Draft</span>
                )}
              </div>
              <h3>{ann.title}</h3>
              <p>{ann.body}</p>
              <p className="announcement-meta">
                Target: {ann.targetAudience} | By: {ann.createdBy}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
